(() => {
  'use strict';

  const root = document.querySelector('[data-tcdkit-kit-builder]');
  if (!root) return;

  document.body.classList.add('tcdkit-kit-builder-active');

  const config = window.TCDKitTheme || {};
  const routes = config.routes || {};
  const validBuilderModes = new Set(['hikoki', 'metabo']);
  const initialMode = new URLSearchParams(window.location.search).get('brand') || root.dataset.defaultBrand;
  let builderMode = validBuilderModes.has(initialMode) ? initialMode : 'hikoki';
  const categoryOrder = [
    'Drilling & driving',
    'Saws & cutting',
    'Grinding, sanding & routing',
    'Nailers & fastening',
    'Outdoor & gardening',
    'Vacuums & site equipment',
    'Lighting, radios & cooling',
    'Other specialist tools',
  ];

  const elements = {
    steps: Array.from(root.querySelectorAll('[data-tcdkit-step]')),
    stepButtons: Array.from(root.querySelectorAll('[data-tcdkit-step-button]')),
    platformCards: Array.from(root.querySelectorAll('[data-tcdkit-platform]')),
    toolGrid: root.querySelector('[data-tcdkit-tool-grid]'),
    toolEmpty: root.querySelector('[data-tcdkit-tool-empty]'),
    toolSearch: root.querySelector('[data-tcdkit-tool-search]'),
    categories: root.querySelector('[data-tcdkit-categories]'),
    results: root.querySelector('[data-tcdkit-results]'),
    powerGrid: root.querySelector('[data-tcdkit-power-grid]'),
    powerEmpty: root.querySelector('[data-tcdkit-power-empty]'),
    powerGuidance: root.querySelector('[data-tcdkit-power-guidance]'),
    progress: root.querySelector('[data-tcdkit-discount-progress]'),
    progressTitle: root.querySelector('[data-tcdkit-discount-title]'),
    progressDetail: root.querySelector('[data-tcdkit-discount-detail]'),
    progressMeter: root.querySelector('[data-tcdkit-discount-meter]'),
    summary: root.querySelector('[data-tcdkit-summary]'),
    summaryCount: root.querySelector('[data-tcdkit-summary-count]'),
    summaryPlatform: root.querySelector('[data-tcdkit-summary-platform]'),
    summaryItems: root.querySelector('[data-tcdkit-summary-items]'),
    summarySaving: root.querySelector('[data-tcdkit-summary-saving]'),
    summaryTotal: root.querySelector('[data-tcdkit-summary-total]'),
    summaryTotalInc: root.querySelector('[data-tcdkit-summary-total-inc]'),
    summaryEditTools: root.querySelector('[data-tcdkit-summary-edit-tools]'),
    summaryAction: root.querySelector('[data-tcdkit-summary-action]'),
    mobileSummary: root.querySelector('[data-tcdkit-mobile-summary]'),
    mobileAction: root.querySelector('[data-tcdkit-mobile-action]'),
    mobileCount: root.querySelector('[data-tcdkit-mobile-count]'),
    mobileSaving: root.querySelector('[data-tcdkit-mobile-saving]'),
    mobileTotal: root.querySelector('[data-tcdkit-mobile-total]'),
    mobileSummaryLabel: root.querySelector('[data-tcdkit-mobile-summary-label]'),
    reviewTools: root.querySelector('[data-tcdkit-review-tools]'),
    reviewPower: root.querySelector('[data-tcdkit-review-power]'),
    reviewSaving: root.querySelector('[data-tcdkit-review-saving]'),
    reviewNormal: root.querySelector('[data-tcdkit-review-normal]'),
    reviewSaved: root.querySelector('[data-tcdkit-review-saved]'),
    reviewTotalEx: root.querySelector('[data-tcdkit-review-total-ex]'),
    reviewTotalInc: root.querySelector('[data-tcdkit-review-total-inc]'),
    addKit: root.querySelector('[data-tcdkit-add-kit]'),
    clearKitButtons: Array.from(root.querySelectorAll('[data-tcdkit-clear-kit]')),
    status: root.querySelector('[data-tcdkit-kit-status]'),
    builderEyebrow: root.querySelector('[data-tcdkit-builder-eyebrow]'),
    builderTitle: root.querySelector('#tcdkit-kit-builder-title'),
    builderIntro: root.querySelector('[data-tcdkit-builder-intro]'),
    fiveRequirement: root.querySelector('[data-tcdkit-five-requirement]'),
    tenRequirement: root.querySelector('[data-tcdkit-ten-requirement]'),
    setupWarnings: Array.from(root.querySelectorAll('[data-tcdkit-setup-warning]')),
    reviewPowerTitle: root.querySelector('[data-tcdkit-review-power-title]'),
  };

  function parseData(selector) {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  const tools = ['hikoki-tools-18v', 'hikoki-tools-36v', 'metabo-tools-18v'].flatMap(key => parseData(`[data-tcdkit-data="${key}"]`)).filter((item) => item.available && validBuilderModes.has(item.brand));
  const powerSets = ['hikoki-power-18v', 'hikoki-power-multivolt', 'metabo-power-18v'].flatMap(key => parseData(`[data-tcdkit-data="${key}"]`)).filter((item) => item.available && validBuilderModes.has(item.brand));
  const toolByVariant = new Map(tools.map((item) => [String(item.variantId), item]));
  const powerByVariant = new Map(powerSets.map((item) => [String(item.variantId), item]));

  const state = {
    platform: '',
    selectedTools: new Map(),
    selectedPowerIds: { hikoki: '', metabo: '' },
    step: 1,
    category: 'All tools',
    search: '',
    busy: false,
    added: false,
  };

  let demoSnapshot = null;

  function setMobileSummaryOpen(open) {
    const shouldOpen = Boolean(open);
    elements.summary.classList.toggle('is-mobile-open', shouldOpen);
    elements.mobileSummary.setAttribute('aria-expanded', String(shouldOpen));
    elements.mobileSummaryLabel.textContent = shouldOpen ? 'Hide kit' : 'View kit';
    document.body.classList.toggle('tcdkit-kit-summary-open', shouldOpen);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function jsonRoute(route, fallback) {
    const value = route || fallback;
    return value.endsWith('.js') ? value : `${value}.js`;
  }

  function formatMoney(minorAmount) {
    const amount = Number(minorAmount || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP',
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function normalisePrice(amount, taxable) {
    const source = Number(amount || 0);
    if (!taxable) return { exVat: source, incVat: source };
    return config.taxesIncluded
      ? { exVat: Math.round(source / (config.vat || 1.2)), incVat: source }
      : { exVat: source, incVat: Math.round(source * (config.vat || 1.2)) };
  }

  function cleanTitle(title) {
    return String(title || '')
      .replace(/^(HiKOKI|Metabo)\s+/i, '')
      .replace(/\s*[-–—]\s*(body[\s-]*only|bare unit)\s*$/i, '')
      .replace(/\s+(body[\s-]*only|bare unit)\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanSku(sku) {
    return String(sku || '').replace(/^'+/, '').trim();
  }

  function categoryFor(item) {
    const source = `${item.type || ''} ${item.title || ''}`.toLowerCase();
    if (/drill|driver|wrench|screw|auger|mixer|magnetic|hammer|rotary|demolition|breaker/.test(source)) return 'Drilling & driving';
    if (/saw|cutter|cutting|shear|nibbler|multi.?tool|threaded rod/.test(source)) return 'Saws & cutting';
    if (/grind|sand|polish|router|planer|die grinder/.test(source)) return 'Grinding, sanding & routing';
    if (/nail|stapl|fasten|caulk|grease gun/.test(source)) return 'Nailers & fastening';
    if (/hedge|grass|brush cutter|strimmer|trimmer|chain.?saw|blower|garden|mower|pruner|earth auger/.test(source)) return 'Outdoor & gardening';
    if (/vacuum|extract|washer|compressor|pump|vibrator|dust/.test(source)) return 'Vacuums & site equipment';
    if (/light|lamp|radio|speaker|fan|cool|heat gun|power charger/.test(source)) return 'Lighting, radios & cooling';
    return 'Other specialist tools';
  }

  tools.forEach((item) => {
    item.brand = String(item.brand || '').toLowerCase();
    item.sku = cleanSku(item.sku);
    item.category = categoryFor(item);
    item.shortTitle = cleanTitle(item.title);
  });

  const powerDescriptions = {
    UC18YSL3JEZ: ['2 × batteries: 5.0Ah (18V) / 2.5Ah (36V)', 'Includes HiKOKI rapid charger'],
    UC18YSL3JFZ: ['2 × batteries: 8.0Ah (18V) / 4.0Ah (36V)', 'Includes HiKOKI rapid charger'],
    UC36YSL2JAZ: ['2 × T-PWR: 8.0Ah (18V) / 4.0Ah (36V)', 'Tabless batteries + SUPER fast charger'],
    UC18YSL3JHZ: ['2 × batteries: 5.0Ah (18V) / 2.5Ah (36V)', 'Includes HiKOKI rapid charger'],
    UC18YSL3JJZ: ['2 × batteries: 8.0Ah (18V) / 4.0Ah (36V)', 'Includes HiKOKI rapid charger'],
    UC18YSL3JKZ: ['2 × 18V 5.0Ah batteries', 'Includes HiKOKI rapid charger'],
    UC18YFSLJEZ: ['2 × 18V 4.0Ah batteries', 'Includes HiKOKI charger'],
    UC18YFSLJFZ: ['2 × 18V 2.0Ah batteries', 'Includes HiKOKI charger'],
    685143000: ['4 × LiHD 10.0Ah batteries', 'ASC 145 DUO charger + metaBOX'],
    685305380: ['2 × LiHDX 8.0Ah batteries', 'ASC 290 charger + metaBOX 145'],
    685303380: ['2 × LiHD 8.0Ah batteries', 'ASC 290 charger + metaBOX 145'],
    685122380: ['2 × LiHD 5.5Ah batteries', 'Includes Metabo ASC 145 charger'],
    685163380: ['2 × LiHD 4.0Ah batteries', 'Includes Metabo ASC 55 charger'],
    685304380: ['2 × LiHD 10.0Ah batteries', 'ASC 290 charger + metaBOX 145'],
  };

  powerSets.forEach((item) => {
    item.brand = String(item.brand || '').toLowerCase();
    item.sku = cleanSku(item.sku);
    const description = powerDescriptions[item.sku] || [cleanTitle(item.title), 'Battery and charger set'];
    item.shortTitle = description[0];
    item.detail = description[1];
  });

  function pushEvent(name, detail = {}) {
    if (demoSnapshot) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
  }

  function requiredPowerBrands() {
    return builderMode === 'both' ? ['hikoki', 'metabo'] : [builderMode];
  }

  function selectedPower(brand) {
    const selectedBrand = brand || requiredPowerBrands()[0];
    return powerByVariant.get(String(state.selectedPowerIds[selectedBrand] || '')) || null;
  }

  function selectedPowers() {
    return requiredPowerBrands().map((brand) => selectedPower(brand)).filter(Boolean);
  }

  function powerComplete() {
    return selectedPowers().length === requiredPowerBrands().length;
  }

  function toolQuantity() {
    return Array.from(state.selectedTools.values()).reduce((total, selection) => total + selection.quantity, 0);
  }

  function toolQuantityForBrand(brand) {
    return Array.from(state.selectedTools.values()).reduce((total, selection) => {
      const item = toolByVariant.get(String(selection.variantId));
      return item?.brand === brand ? total + selection.quantity : total;
    }, 0);
  }

  function toolsComplete() {
    if (toolQuantity() < 2) return false;
    return builderMode !== 'both' || (toolQuantityForBrand('hikoki') > 0 && toolQuantityForBrand('metabo') > 0);
  }

  function discountLevel() {
    if (!powerComplete() || !toolsComplete()) return 0;
    const quantity = toolQuantity();
    if (quantity >= 4) return 10;
    if (quantity >= 2) return 5;
    return 0;
  }

  function compatibleTool(item) {
    if (!item || (builderMode !== 'both' && item.brand !== builderMode)) return false;
    if (builderMode === 'metabo' || item.brand === 'metabo') return item.platform === '18v';
    if (state.platform === 'mixed') return item.platform === '18v' || item.platform === '36v';
    return item.platform === state.platform;
  }

  function compatiblePower(item) {
    if (!item || !requiredPowerBrands().includes(item.brand)) return false;
    if (item.brand === 'metabo') return item.platform === '18v';
    if (state.platform === '18v') return item.platform === '18v' || item.platform === 'multivolt';
    return (state.platform === '36v' || state.platform === 'mixed') && item.platform === 'multivolt';
  }

  function calculateTotals() {
    const level = discountLevel();
    const lines = [];
    state.selectedTools.forEach((selection) => {
      const item = toolByVariant.get(String(selection.variantId));
      if (item) lines.push({ item, quantity: selection.quantity });
    });
    selectedPowers().forEach((power) => lines.push({ item: power, quantity: 1 }));

    return lines.reduce((totals, line) => {
      const source = Number(line.item.price || 0) * line.quantity;
      const discounted = source - Math.round(source * level / 100);
      const normal = normalisePrice(source, line.item.taxable);
      const reduced = normalisePrice(discounted, line.item.taxable);
      totals.normalEx += normal.exVat;
      totals.normalInc += normal.incVat;
      totals.totalEx += reduced.exVat;
      totals.totalInc += reduced.incVat;
      return totals;
    }, { normalEx: 0, normalInc: 0, totalEx: 0, totalInc: 0 });
  }

  function storageKey() {
    return `tcdkit-kit-builder-v2-${builderMode}`;
  }

  function saveState() {
    if (demoSnapshot) return;
    try {
      sessionStorage.setItem(storageKey(), JSON.stringify({
        platform: state.platform,
        selectedTools: Array.from(state.selectedTools.values()),
        selectedPowerIds: state.selectedPowerIds,
        step: state.step,
      }));
    } catch (error) {
      // The builder remains fully usable when session storage is unavailable.
    }
  }

  function restoreState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey()) || '{}');
      if (['18v', '36v', 'mixed'].includes(saved.platform)) state.platform = saved.platform;
      (Array.isArray(saved.selectedTools) ? saved.selectedTools : []).forEach((selection) => {
        const item = toolByVariant.get(String(selection.variantId));
        if (item && compatibleTool(item)) {
          state.selectedTools.set(String(item.variantId), {
            variantId: item.variantId,
            quantity: Math.min(selectionLimit(item), Math.max(1, Math.floor(Number(selection.quantity) || 1))),
          });
        }
      });
      const savedPowerIds = saved.selectedPowerIds || (saved.selectedPowerId ? { hikoki: saved.selectedPowerId } : {});
      requiredPowerBrands().forEach((brand) => {
        const power = powerByVariant.get(String(savedPowerIds[brand] || ''));
        if (power && compatiblePower(power)) state.selectedPowerIds[brand] = String(power.variantId);
      });
      state.step = Math.min(Math.max(Number(saved.step || 1), 1), maximumStep());
    } catch (error) {
      state.step = 1;
    }
  }

  function maximumStep() {
    if (!state.platform) return 1;
    if (!toolsComplete()) return 2;
    if (!powerComplete()) return 3;
    return 4;
  }

  function notifyGuide() {
    root.dispatchEvent(new CustomEvent('tcdkit:guide-state', { detail: {
      mode: builderMode, step: state.step, platform: state.platform,
      toolsComplete: toolsComplete(), powerComplete: powerComplete(),
      busy: state.busy, added: state.added, demo: Boolean(demoSnapshot),
    } }));
  }

  function setStep(nextStep, focus = true) {
    setMobileSummaryOpen(false);
    const allowedStep = Math.min(Math.max(Number(nextStep || 1), 1), demoSnapshot ? 4 : maximumStep());
    state.step = allowedStep;
    elements.steps.forEach((section) => {
      const active = Number(section.dataset.tcdkitStep) === allowedStep;
      section.hidden = !active;
      section.classList.toggle('is-active', active);
    });
    elements.stepButtons.forEach((button) => {
      const step = Number(button.dataset.tcdkitStepButton);
      const current = step === allowedStep;
      const complete = step < allowedStep;
      button.disabled = step > maximumStep();
      button.classList.toggle('is-current', current);
      button.classList.toggle('is-complete', complete);
      button.removeAttribute('aria-current');
      if (current) button.setAttribute('aria-current', 'step');
      button.closest('li')?.classList.toggle('is-complete', complete);
    });
    if (allowedStep === 2) renderTools();
    if (allowedStep === 3) renderPowerSets();
    if (allowedStep === 4) renderReview();
    renderNavigation();
    saveState();
    if (focus) {
      elements.steps.find((section) => Number(section.dataset.tcdkitStep) === allowedStep)
        ?.querySelector('h2')?.focus?.({ preventScroll: true });
      root.querySelector('.tcdkit-kit-builder__progress-shell')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }
    notifyGuide();
  }

  function renderNavigation() {
    elements.steps.forEach((section) => {
      const step = Number(section.dataset.tcdkitStep);
      const next = section.querySelector('[data-tcdkit-next]');
      if (!next) return;
      if (step === 1) next.disabled = !state.platform;
      if (step === 2) next.disabled = !toolsComplete();
      if (step === 3) next.disabled = !powerComplete();
    });
    renderJourneyActions();
  }

  function journeyAction() {
    const quantity = toolQuantity();
    const completePowerSetup = powerComplete();
    const level = discountLevel();
    if (state.added) return { label: 'Clear kit & build another', disabled: false, action: 'clear' };
    if (level && completePowerSetup && toolsComplete()) {
      return { label: `Add kit to cart · ${level}% off`, disabled: state.busy, action: 'cart' };
    }
    if (!state.platform) return { label: 'Choose a platform', disabled: true, action: 'none' };
    if (!toolsComplete()) {
      if (builderMode === 'both' && quantity >= 1) {
        const missingBrand = toolQuantityForBrand('hikoki') ? 'Metabo' : 'HiKOKI';
        return {
          label: `Add a ${missingBrand} tool`,
          disabled: state.step === 1 ? false : true,
          action: state.step === 1 ? 'tools' : 'none',
        };
      }
      const remaining = 2 - quantity;
      return {
        label: remaining === 1 ? 'Add 1 more tool' : 'Choose your tools',
        disabled: state.step === 1 ? false : true,
        action: state.step === 1 ? 'tools' : 'none',
      };
    }
    if (!completePowerSetup) return {
      label: builderMode === 'both' ? 'Choose both power sets' : 'Choose a power set',
      disabled: state.step === 3,
      action: state.step === 3 ? 'none' : 'power',
    };
    return { label: 'Review your kit', disabled: false, action: 'review' };
  }

  function renderJourneyActions() {
    const action = journeyAction();
    [elements.summaryAction, elements.mobileAction].forEach((button) => {
      if (!button) return;
      button.textContent = action.label;
      button.disabled = action.disabled;
      button.dataset.tcdkitJourneyAction = action.action;
      button.classList.toggle('is-cart-ready', action.action === 'cart');
      button.classList.toggle('is-reset', action.action === 'clear');
    });
  }

  function setPlatformCardCopy(platform, copy) {
    const card = elements.platformCards.find((item) => item.dataset.tcdkitPlatform === platform);
    if (!card || !copy) return;
    const small = card.querySelector('.tcdkit-platform-card__copy small');
    const strong = card.querySelector('.tcdkit-platform-card__copy strong');
    const description = card.querySelector('.tcdkit-platform-card__copy > span');
    if (small) small.textContent = copy.small;
    if (strong) strong.textContent = copy.title;
    if (description) description.textContent = copy.description;
  }

  function renderModeCopy() {
    const isMetabo = builderMode === 'metabo';
    const isBoth = builderMode === 'both';
    root.dataset.tcdkitBuilderMode = builderMode;
    root.querySelectorAll('[data-tcdkit-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.tcdkitMode === builderMode)));

    elements.builderEyebrow.textContent = isBoth
      ? 'Build a HiKOKI + Metabo cordless kit'
      : isMetabo ? 'Build your Metabo cordless kit' : 'Build your HiKOKI cordless kit';
    elements.builderTitle.textContent = isBoth ? 'Two tool systems. One serious kit.' : 'Choose the tools. Build the saving.';
    elements.builderIntro.textContent = isBoth
      ? 'Choose at least one tool from each brand, then add a dedicated HiKOKI power set and a dedicated Metabo CAS power set. Every qualifying item receives the same automatic kit saving.'
      : isMetabo
        ? 'Build around Metabo CAS 18V body-only tools, then choose one compatible Metabo battery and charger set. Every qualifying item receives the same automatic kit saving.'
        : 'Start with your battery platform, add the tools you need, then choose one compatible battery and charger set. Every qualifying item receives the same automatic kit saving.';
    elements.fiveRequirement.textContent = isBoth ? '2 tools + 2 brand power sets' : '2 tools + 1 power set';
    elements.tenRequirement.textContent = isBoth ? '4+ tools + 2 brand power sets' : '4+ tools + 1 power set';

    const platformTitle = root.querySelector('#tcdkit-kit-step-1-title');
    const platformGuidance = platformTitle?.nextElementSibling;
    const why = root.querySelector('.tcdkit-kit-builder__why');
    if (platformTitle) platformTitle.textContent = isBoth ? 'Choose the HiKOKI side of your kit' : isMetabo ? 'Choose Metabo CAS 18V' : 'Choose your battery platform';
    if (platformGuidance) platformGuidance.textContent = isBoth
      ? 'Metabo tools use CAS 18V; choose whether the HiKOKI side is 18V, 36V or mixed Multi Volt.'
      : isMetabo ? 'Your builder will show compatible Metabo CAS body-only tools and power sets.' : 'This controls which tools and power sets you can combine safely.';
    if (why) {
      why.querySelector('strong').textContent = isBoth ? 'Why two power sets are essential' : isMetabo ? 'Why CAS comes first' : 'Why platform comes first';
      why.querySelector('p').textContent = isBoth
        ? 'HiKOKI and Metabo batteries are different physical systems. A combined kit therefore includes a dedicated power set for each brand.'
        : isMetabo
          ? 'Metabo CAS 18V batteries power compatible Metabo 18V machines and other tools within the Cordless Alliance System.'
          : 'Multi Volt batteries work in compatible HiKOKI 18V and 36V tools. Standard 18V batteries cannot power 36V machines.';
    }

    setPlatformCardCopy('18v', isBoth ? {
      small: 'Two agile 18V platforms',
      title: 'Dual 18V kit',
      description: 'Combine HiKOKI 18V tools with Metabo CAS 18V tools and one power set for each brand.',
    } : isMetabo ? {
      small: 'Cordless Alliance System',
      title: 'Metabo CAS 18V kit',
      description: 'Choose Metabo 18V body-only tools and finish with a dedicated CAS battery and charger set.',
    } : {
      small: 'Agile professional platform',
      title: '18V cordless kit',
      description: 'Choose from 18V body-only tools. Finish with an 18V or Multi Volt power set.',
    });
    setPlatformCardCopy('36v', isBoth ? {
      small: 'High-output HiKOKI + CAS',
      title: 'HiKOKI 36V + Metabo 18V',
      description: 'Pair HiKOKI 36V Multi Volt performance with Metabo CAS 18V machines.',
    } : {
      small: 'Maximum cordless output',
      title: '36V Multi Volt kit',
      description: 'Choose from high-output 36V tools. Finish with a Multi Volt power set.',
    });
    setPlatformCardCopy('mixed', isBoth ? {
      small: 'The broadest tool choice',
      title: 'HiKOKI Multi Volt + Metabo CAS',
      description: 'Mix HiKOKI 18V and 36V tools alongside Metabo CAS 18V tools, with both required power sets.',
    } : {
      small: 'One Multi Volt battery system',
      title: 'Mixed Multi Volt kit',
      description: 'Combine compatible 18V and 36V tools around one flexible Multi Volt battery system.',
    });

    elements.platformCards.forEach((card) => {
      const unavailable = isMetabo && card.dataset.tcdkitPlatform !== '18v';
      card.hidden = unavailable;
      card.disabled = unavailable;
      if (unavailable) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
    });
    const toolTitle = root.querySelector('#tcdkit-kit-step-2-title');
    const toolGuidance = toolTitle?.nextElementSibling;
    if (toolTitle) toolTitle.textContent = isBoth ? 'Choose tools from both brands' : isMetabo ? 'Choose your Metabo tools' : 'Choose your HiKOKI tools';
    if (toolGuidance) toolGuidance.textContent = isBoth
      ? 'Select at least two tools in total, including at least one HiKOKI and one Metabo tool.'
      : 'Select at least two tools to unlock the first saving level.';
    const powerTitle = root.querySelector('#tcdkit-kit-step-3-title');
    if (powerTitle) powerTitle.textContent = isBoth ? 'Choose both battery and charger sets' : 'Choose one battery and charger set';
    if (elements.reviewPowerTitle) {
      elements.reviewPowerTitle.textContent = isBoth ? 'Your two power sets' : 'Your power set';
      const editButton = elements.reviewPowerTitle.parentElement?.querySelector('[data-tcdkit-edit-step="3"]');
      if (editButton) editButton.textContent = isBoth ? 'Change sets' : 'Change set';
    }
    elements.setupWarnings.forEach((warning) => {
      warning.hidden = builderMode !== 'both' && warning.dataset.tcdkitSetupWarning !== builderMode;
    });
  }

  function renderPlatforms() {
    elements.platformCards.forEach((card) => {
      const selected = card.dataset.tcdkitPlatform === state.platform;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-checked', String(selected));
    });
  }

  function availableTools() {
    return tools.filter(compatibleTool);
  }

  function renderCategories() {
    const platformTools = availableTools();
    const categories = categoryOrder.filter((category) => platformTools.some((item) => item.category === category));
    if (state.category !== 'All tools' && !categories.includes(state.category)) state.category = 'All tools';
    elements.categories.innerHTML = ['All tools', ...categories].map((category) => {
      const count = category === 'All tools'
        ? platformTools.length
        : platformTools.filter((item) => item.category === category).length;
      return `<button type="button" class="${state.category === category ? 'is-active' : ''}" data-tcdkit-category="${escapeHtml(category)}">${escapeHtml(category)} <span>(${count})</span></button>`;
    }).join('');
  }

  function filteredTools() {
    const query = state.search.trim().toLowerCase();
    return availableTools().filter((item) => {
      if (state.category !== 'All tools' && item.category !== state.category) return false;
      if (!query) return true;
      return `${item.title} ${item.sku} ${item.type}`.toLowerCase().includes(query);
    });
  }

  function selectionLimit(item) {
    return Math.max(1, Math.min(20, Number(item.maxQuantity) || 20));
  }

  function availabilityMarkup(item) {
    return `<p class="tcdkit-availability tcdkit-availability--${escapeHtml(item.stockState || 'order')}">${escapeHtml(item.stockNotice || 'Check current availability on the product page.')}</p>`;
  }

  function toolCard(item) {
    const selected = state.selectedTools.has(String(item.variantId));
    const price = normalisePrice(item.price, item.taxable);
    const brandLabel = item.brand === 'metabo' ? 'Metabo' : 'HiKOKI';
    return `
      <article class="tcdkit-kit-product tcdkit-kit-product--${item.brand} ${selected ? 'is-selected' : ''}" data-tcdkit-tool-card="${item.variantId}" data-tcdkit-toggle-tool="${item.variantId}">
        <div class="tcdkit-kit-product__image">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="300" height="225">` : ''}
          <span class="tcdkit-kit-product__badge">${brandLabel} · ${escapeHtml(item.platform.toUpperCase())} body only</span>
          <span class="tcdkit-kit-product__selected-mark" aria-hidden="true">✓</span>
        </div>
        <div class="tcdkit-kit-product__content">
          <span class="tcdkit-kit-product__sku">${escapeHtml(item.sku || brandLabel)}</span>
          <h3>${escapeHtml(item.shortTitle)}</h3>
          ${availabilityMarkup(item)}
          <div class="tcdkit-kit-product__price">
            <strong>${formatMoney(price.exVat)}</strong><span>ex VAT</span>
            <span>${formatMoney(price.incVat)} inc VAT</span>
          </div>
          <button class="tcdkit-kit-product__add" type="button" data-tcdkit-toggle-tool="${item.variantId}" aria-pressed="${selected}">
            ${selected ? '✓ Added to kit' : '+ Add to kit'}
          </button>
          <a class="tcdkit-kit-product__details" href="${escapeHtml(item.url)}" data-tcdkit-view-product>View full item details <span aria-hidden="true">↗</span></a>
        </div>
      </article>`;
  }

  function renderTools() {
    renderCategories();
    const items = filteredTools();
    elements.toolGrid.innerHTML = items.map(toolCard).join('');
    elements.toolEmpty.hidden = items.length > 0;
    const selectedDetail = builderMode === 'both'
      ? `${toolQuantity()} selected · ${toolQuantityForBrand('hikoki')} HiKOKI · ${toolQuantityForBrand('metabo')} Metabo`
      : `${toolQuantity()} selected`;
    elements.results.textContent = `${items.length} ${items.length === 1 ? 'tool' : 'tools'} shown · ${selectedDetail}`;
    if (elements.toolSearch && elements.toolSearch.value !== state.search) elements.toolSearch.value = state.search;
    renderNavigation();
  }

  function powerCard(item) {
    const selected = String(item.variantId) === String(state.selectedPowerIds[item.brand]);
    const price = normalisePrice(item.price, item.taxable);
    const brandLabel = item.brand === 'metabo' ? 'Metabo' : 'HiKOKI';
    const compatibility = item.brand === 'metabo'
      ? 'For Metabo CAS 18V tools'
      : item.platform === 'multivolt'
        ? 'Works with compatible HiKOKI 18V and 36V tools'
        : 'For HiKOKI 18V tools only';
    return `
      <article class="tcdkit-power-card tcdkit-power-card--${item.brand} ${selected ? 'is-selected' : ''}" data-tcdkit-power-card="${item.variantId}" data-tcdkit-choose-power="${item.variantId}" role="radio" aria-checked="${selected}">
        <div class="tcdkit-power-card__image">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="300" height="225">` : ''}
          <span class="tcdkit-power-card__badge">${brandLabel} · ${item.platform === 'multivolt' ? 'Multi Volt' : '18V'}</span>
        </div>
        <div class="tcdkit-power-card__content">
          <span class="tcdkit-power-card__sku">${escapeHtml(item.sku || `${brandLabel} power set`)}</span>
          <h3>${escapeHtml(item.shortTitle)}</h3>
          ${availabilityMarkup(item)}
          <p class="tcdkit-power-card__detail">${escapeHtml(item.detail)}</p>
          <span class="tcdkit-power-card__compatible">${escapeHtml(compatibility)}</span>
          <div class="tcdkit-power-card__price">
            <strong>${formatMoney(price.exVat)}</strong><span>ex VAT</span>
            <span>${formatMoney(price.incVat)} inc VAT</span>
          </div>
          <button class="tcdkit-power-card__choose" type="button" data-tcdkit-choose-power="${item.variantId}">
            ${selected ? '✓ Selected power set' : 'Choose this power set'}
          </button>
          <a class="tcdkit-kit-product__details" href="${escapeHtml(item.url)}" data-tcdkit-view-product>View full set details <span aria-hidden="true">↗</span></a>
        </div>
      </article>`;
  }

  function renderPowerSets() {
    const compatible = powerSets.filter(compatiblePower);
    requiredPowerBrands().forEach((brand) => {
      const selectedId = state.selectedPowerIds[brand];
      const selectionIsValid = compatible.some((item) => item.brand === brand && String(item.variantId) === String(selectedId));
      if (selectedId && !selectionIsValid) state.selectedPowerIds[brand] = '';
    });
    elements.powerGrid.innerHTML = requiredPowerBrands().map((brand) => {
      const items = compatible.filter((item) => item.brand === brand);
      const brandLabel = brand === 'metabo' ? 'Metabo CAS power set' : 'HiKOKI power set';
      const detail = brand === 'metabo'
        ? 'Choose the batteries and charger that will power the Metabo side of your kit.'
        : 'Choose the batteries and charger that will power the HiKOKI side of your kit.';
      return `
        <section class="tcdkit-power-brand-group tcdkit-power-brand-group--${brand}" aria-labelledby="tcdkit-power-${brand}">
          <div class="tcdkit-power-brand-group__heading">
            <div><span>${brand === 'metabo' ? 'CAS 18V' : state.platform === '18v' ? '18V / Multi Volt' : 'Multi Volt'}</span><h3 id="tcdkit-power-${brand}">${brandLabel}</h3></div>
            <p>${detail}</p>
          </div>
          <div class="tcdkit-power-brand-grid" role="radiogroup" aria-labelledby="tcdkit-power-${brand}">
            ${items.length ? items.map(powerCard).join('') : `<div class="tcdkit-power-brand-group__empty">No compatible ${brandLabel.toLowerCase()} is currently available.</div>`}
          </div>
        </section>`;
    }).join('');
    elements.powerEmpty.hidden = compatible.length > 0;
    elements.powerGuidance.textContent = builderMode === 'both'
      ? 'A combined kit needs one dedicated power set for each brand; the two battery systems are not interchangeable.'
      : builderMode === 'metabo'
        ? 'Choose one Metabo CAS 18V battery and charger set.'
        : state.platform === '18v'
          ? 'Choose an 18V set or step up to a flexible Multi Volt set.'
          : 'Only Multi Volt sets are shown because your kit contains 36V tools.';
    renderNavigation();
  }

  function summaryLine(item, quantity, role) {
    const price = normalisePrice(Number(item.price) * quantity, item.taxable);
    return `
      <div class="tcdkit-kit-summary-item">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="62" height="62">` : '<span></span>'}
        <div class="tcdkit-kit-summary-item__copy">
          <strong>${escapeHtml(item.shortTitle || cleanTitle(item.title))}</strong>
          <span>${role === 'tool' ? `${item.brand === 'metabo' ? 'Metabo' : 'HiKOKI'} · ${escapeHtml(item.sku || '')} · Qty ${quantity}` : `${item.brand === 'metabo' ? 'Metabo' : 'HiKOKI'} · ${escapeHtml(item.sku || '')} · Power set`}</span>
        </div>
        <span class="tcdkit-kit-summary-item__price">${formatMoney(price.exVat)}</span>
      </div>`;
  }

  function renderProgress() {
    const quantity = toolQuantity();
    const completeTools = toolsComplete();
    const completePowerSetup = powerComplete();
    const level = discountLevel();
    let title = builderMode === 'both' ? 'Add tools from both brands to unlock 5%' : 'Add 2 tools and a power set to unlock 5%';
    let detail = builderMode === 'both' ? 'Your saving starts with one tool and one power set from each brand.' : 'Your saving updates as you build.';

    if (level === 10) {
      title = '10% kit saving unlocked';
      detail = `Every selected tool and ${builderMode === 'both' ? 'both power sets receive' : 'the power set receives'} 10% off.`;
    } else if (level === 5) {
      const remaining = Math.max(0, 4 - quantity);
      title = '5% kit saving unlocked';
      detail = `Add ${remaining} more ${remaining === 1 ? 'tool' : 'tools'} to unlock 10% off every kit item.`;
    } else if (quantity >= 4 && completeTools && !completePowerSetup) {
      title = '10% is ready to unlock';
      detail = builderMode === 'both' ? 'Choose both compatible power sets to apply it to every kit item.' : 'Choose one compatible power set to apply it to every kit item.';
    } else if (quantity >= 2 && completeTools && !completePowerSetup) {
      title = '5% is ready to unlock';
      detail = `${Math.max(0, 4 - quantity)} more ${Math.max(0, 4 - quantity) === 1 ? 'tool' : 'tools'} reaches 10%; choose ${builderMode === 'both' ? 'both power sets' : 'a power set'} to apply the saving.`;
    } else if (builderMode === 'both' && quantity >= 2 && !completeTools) {
      const missingBrand = toolQuantityForBrand('hikoki') ? 'Metabo' : 'HiKOKI';
      title = `Add a ${missingBrand} tool to qualify`;
      detail = 'A combined kit must contain at least one tool from each brand.';
    } else if (quantity === 1) {
      title = 'Add 1 more tool to unlock 5%';
      detail = builderMode === 'both' ? 'Make it a tool from the other brand, then choose both power sets.' : 'Then choose a compatible battery and charger set.';
    }

    elements.progress.classList.toggle('is-five', level === 5);
    elements.progress.classList.toggle('is-ten', level === 10);
    elements.progressTitle.textContent = title;
    elements.progressDetail.textContent = detail;
    elements.progressMeter.style.width = `${Math.min(quantity, 4) / 4 * 100}%`;
  }

  function renderSummary() {
    const lines = [];
    state.selectedTools.forEach((selection) => {
      const item = toolByVariant.get(String(selection.variantId));
      if (item) lines.push(summaryLine(item, selection.quantity, 'tool'));
    });
    const powers = selectedPowers();
    powers.forEach((power) => lines.push(summaryLine(power, 1, 'set')));
    elements.summaryItems.innerHTML = lines.length
      ? lines.join('')
      : `<div class="tcdkit-kit-summary__empty"><span aria-hidden="true">+</span><p>Your selected tools and ${builderMode === 'both' ? 'power sets' : 'power set'} will appear here.</p></div>`;

    const quantity = toolQuantity();
    const itemCount = quantity + powers.length;
    const level = discountLevel();
    const totals = calculateTotals();
    elements.summaryCount.textContent = itemCount ? `${itemCount} ${itemCount === 1 ? 'item' : 'items'} selected` : 'No items selected';
    elements.summaryPlatform.textContent = state.platform
      ? builderMode === 'metabo' ? 'Metabo CAS 18V'
        : builderMode === 'both' ? `HiKOKI ${state.platform === 'mixed' ? '18V + 36V' : state.platform.toUpperCase()} + Metabo CAS 18V`
          : state.platform === 'mixed' ? '18V + 36V mixed' : `${state.platform.toUpperCase()} platform`
      : 'Choose a platform';
    elements.summarySaving.textContent = level
      ? `${level}% unlocked across every kit item — saving ${formatMoney(totals.normalEx - totals.totalEx)} ex VAT.`
      : toolsComplete() && !powerComplete() ? `Choose ${builderMode === 'both' ? 'both power sets' : 'a power set'} to activate your kit saving.` : 'Build a kit to unlock up to 10% off.';
    elements.summarySaving.classList.toggle('is-ten', level === 10);
    elements.summaryTotal.textContent = `${formatMoney(totals.totalEx)} ex VAT`;
    elements.summaryTotalInc.textContent = `${formatMoney(totals.totalInc)} inc VAT`;
    elements.mobileCount.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    elements.mobileSaving.textContent = level ? `${level}% kit saving unlocked` : 'Up to 10% kit saving';
    elements.mobileTotal.textContent = `${formatMoney(totals.totalEx)} ex VAT`;
    elements.summaryEditTools.hidden = !state.platform;
    if (state.platform) {
      const remaining = Math.max(0, 4 - quantity);
      elements.summaryEditTools.textContent = level === 5 && remaining
        ? `Add ${remaining} more ${remaining === 1 ? 'tool' : 'tools'} to unlock 10%`
        : quantity ? 'Add or change tools' : 'Choose your tools';
    }
    const canClear = Boolean(state.platform || state.selectedTools.size || selectedPowers().length);
    elements.clearKitButtons.forEach((button) => {
      button.hidden = !canClear || (button.classList.contains('tcdkit-kit-clear') && !state.added);
    });
    renderJourneyActions();
  }

  function reviewLine(item, quantity, role) {
    const price = normalisePrice(Number(item.price) * quantity, item.taxable);
    const quantityControl = role === 'tool' ? `
      <div class="tcdkit-kit-quantity" aria-label="Quantity for ${escapeHtml(item.shortTitle)}">
        <button type="button" data-tcdkit-quantity="minus" data-variant-id="${item.variantId}" aria-label="Reduce quantity">−</button>
        <span>${quantity}</span>
        <button type="button" data-tcdkit-quantity="plus" data-variant-id="${item.variantId}" aria-label="Increase quantity">+</button>
      </div>` : '';
    return `
      <div class="tcdkit-kit-review-item">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="76" height="76">` : '<span></span>'}
        <div class="tcdkit-kit-review-item__copy">
          <strong>${escapeHtml(item.shortTitle || cleanTitle(item.title))}</strong>
          <span>${item.brand === 'metabo' ? 'Metabo' : 'HiKOKI'} · ${escapeHtml(item.sku || '')} · ${role === 'tool' ? `${item.platform.toUpperCase()} body only` : item.platform === 'multivolt' ? 'Multi Volt power set' : '18V power set'}</span>
        </div>
        <div class="tcdkit-kit-review-item__end">
          <strong>${formatMoney(price.exVat)} ex VAT</strong>
          ${quantityControl}
        </div>
      </div>`;
  }

  function renderReview() {
    const toolLines = [];
    state.selectedTools.forEach((selection) => {
      const item = toolByVariant.get(String(selection.variantId));
      if (item) toolLines.push(reviewLine(item, selection.quantity, 'tool'));
    });
    elements.reviewTools.innerHTML = toolLines.join('');
    const powers = selectedPowers();
    elements.reviewPower.innerHTML = powers.map((power) => reviewLine(power, 1, 'set')).join('');

    const level = discountLevel();
    const totals = calculateTotals();
    elements.reviewSaving.textContent = level
      ? `${level}% KIT SAVING UNLOCKED — every item below receives ${level}% off automatically.`
      : builderMode === 'both' ? 'Add tools from both brands and one compatible power set for each brand to unlock 5%.' : 'Add at least two tools and one compatible power set to unlock 5%.';
    elements.reviewNormal.textContent = `${formatMoney(totals.normalEx)} ex VAT`;
    elements.reviewSaved.textContent = `−${formatMoney(totals.normalEx - totals.totalEx)} ex VAT`;
    elements.reviewTotalEx.textContent = `${formatMoney(totals.totalEx)} ex VAT`;
    elements.reviewTotalInc.textContent = `${formatMoney(totals.totalInc)} inc VAT`;
    elements.addKit.disabled = state.busy || state.added || !toolsComplete() || !powerComplete();
    elements.addKit.textContent = state.added ? 'Kit added to cart' : state.busy ? 'Adding your kit…' : 'Add custom kit to cart';
    renderJourneyActions();
  }

  function renderAll() {
    renderModeCopy();
    renderPlatforms();
    renderProgress();
    renderSummary();
    renderNavigation();
    if (state.step === 2) renderTools();
    if (state.step === 3) renderPowerSets();
    if (state.step === 4) renderReview();
    saveState();
    notifyGuide();
  }

  function markChanged() {
    state.added = false;
    elements.status.textContent = '';
  }

  function setBuilderMode(nextMode) {
    if (demoSnapshot) return;
    if (state.busy) return;
    if (!validBuilderModes.has(nextMode) || nextMode === builderMode) return;
    saveState();
    builderMode = nextMode;
    hideKitFeedback();
    state.platform = '';
    state.selectedTools.clear();
    state.selectedPowerIds = { hikoki: '', metabo: '' };
    state.step = 1;
    state.category = 'All tools';
    state.search = '';
    state.busy = false;
    state.added = false;
    elements.status.textContent = '';
    restoreState();
    if (builderMode === 'metabo' && state.platform && state.platform !== '18v') state.platform = '18v';
    setMobileSummaryOpen(false);
    renderAll();
    setStep(state.step, false);
  }

  function clearKit() {
    if (state.busy) return;
    const hasSelections = Boolean(state.selectedTools.size || selectedPowers().length);
    if (hasSelections && !state.added) {
      const confirmed = window.confirm('Clear this kit and start again?');
      if (!confirmed) return;
    }
    hideKitFeedback();
    state.platform = '';
    state.selectedTools.clear();
    state.selectedPowerIds = { hikoki: '', metabo: '' };
    state.step = 1;
    state.category = 'All tools';
    state.search = '';
    state.busy = false;
    state.added = false;
    elements.status.textContent = '';
    setMobileSummaryOpen(false);
    try {
      sessionStorage.removeItem(storageKey());
    } catch (error) {
      // The reset still works when session storage is unavailable.
    }
    renderAll();
    setStep(1);
    pushEvent('tcdkit_kit_builder_clear');
  }

  function runJourneyAction(button) {
    const action = button?.dataset?.tcdkitJourneyAction;
    if (!action || button.disabled) return;
    if (action === 'clear') clearKit();
    if (action === 'cart') addKitToCart();
    if (action === 'tools') setStep(2);
    if (action === 'power') setStep(3);
    if (action === 'review') setStep(4);
  }

  function choosePlatform(platform) {
    if (state.busy) return;
    if (!['18v', '36v', 'mixed'].includes(platform)) return;
    if (state.platform && state.platform !== platform && (state.selectedTools.size || selectedPowers().length)) {
      const confirmed = window.confirm(`Changing platform will clear the tools and ${builderMode === 'both' ? 'power sets' : 'power set'} already selected. Continue?`);
      if (!confirmed) return;
      state.selectedTools.clear();
      state.selectedPowerIds = { hikoki: '', metabo: '' };
    }
    state.platform = platform;
    state.category = 'All tools';
    state.search = '';
    markChanged();
    renderAll();
    pushEvent('tcdkit_kit_builder_platform', { tcdkit_platform: platform });
    root.dispatchEvent(new CustomEvent('tcdkit:guide-platform-picked'));
  }

  function toggleTool(variantId) {
    if (state.busy) return;
    const key = String(variantId);
    const item = toolByVariant.get(key);
    if (!item || !compatibleTool(item)) return;
    if (state.selectedTools.has(key)) {
      state.selectedTools.delete(key);
      pushEvent('tcdkit_kit_builder_tool_remove', { tcdkit_variant_id: item.variantId, tcdkit_sku: item.sku });
    } else {
      state.selectedTools.set(key, { variantId: item.variantId, quantity: 1 });
      pushEvent('tcdkit_kit_builder_tool_add', { tcdkit_variant_id: item.variantId, tcdkit_sku: item.sku });
    }
    if (!toolsComplete() && state.step > 2) state.step = 2;
    markChanged();
    renderAll();
  }

  function choosePower(variantId) {
    if (state.busy) return;
    const item = powerByVariant.get(String(variantId));
    if (!item || !compatiblePower(item)) return;
    const previousLevel = discountLevel();
    state.selectedPowerIds[item.brand] = String(item.variantId);
    markChanged();
    renderAll();
    const nextLevel = discountLevel();
    pushEvent('tcdkit_kit_builder_power_set', { tcdkit_variant_id: item.variantId, tcdkit_sku: item.sku, tcdkit_discount_percent: nextLevel });
    if (nextLevel > previousLevel) pushEvent('tcdkit_kit_builder_discount_unlock', { tcdkit_discount_percent: nextLevel });
  }

  function changeQuantity(variantId, direction) {
    if (state.busy) return;
    const key = String(variantId);
    const selection = state.selectedTools.get(key);
    if (!selection) return;
    const previousLevel = discountLevel();
    if (direction === 'plus') selection.quantity = Math.min(selection.quantity + 1, selectionLimit(toolByVariant.get(key)));
    if (direction === 'minus') {
      selection.quantity -= 1;
      if (selection.quantity < 1) state.selectedTools.delete(key);
    }
    markChanged();
    renderAll();
    const nextLevel = discountLevel();
    if (nextLevel > previousLevel) pushEvent('tcdkit_kit_builder_discount_unlock', { tcdkit_discount_percent: nextLevel });
  }

  root.querySelectorAll('[data-tcdkit-mode]').forEach(button => button.addEventListener('click', () => {
    if (state.busy) return;
    setBuilderMode(button.dataset.tcdkitMode);
    root.dispatchEvent(new CustomEvent('tcdkit:guide-brand-picked'));
  }));

  function createKitId() {
    const randomPart = window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(2))).map((value) => value.toString(36)).join('')
      : Math.random().toString(36).slice(2);
    return `tcdkit-kit-${Date.now().toString(36)}-${randomPart}`;
  }

  function appliedLineSaving(item) {
    const original = Number(item.original_line_price);
    const final = Number(item.final_line_price);
    const priceSaving = item.final_line_price != null && Number.isFinite(original) && Number.isFinite(final)
      ? Math.max(0, original - final) : 0;
    const allocations = (item.line_level_discount_allocations || []).reduce((total, allocation) => total + (Number(allocation.amount) || 0), 0);
    return Math.max(0, Number(item.line_level_total_discount) || 0, allocations, priceSaving);
  }

  let kitFeedback = null;
  let basketPulseTimer;
  let feedbackDockObserver;

  function hideKitFeedback() {
    if (kitFeedback) kitFeedback.hidden = true;
  }

  function showKitFeedback(savingConfirmed, level, refreshFailed = false) {
    const copy = parseData('[data-tcdkit-cart-feedback-copy]');
    if (!copy.title) return;
    if (!kitFeedback) {
      kitFeedback = document.createElement('aside');
      kitFeedback.className = 'tcdkit-cart-feedback';
      kitFeedback.setAttribute('aria-label', copy.title);
      kitFeedback.innerHTML = `<svg class="tcdkit-cart-feedback__icon" width="48" height="48" viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="21"></circle><path class="tcdkit-cart-feedback__tick" d="m14 24 7 7 14-15"></path><path class="tcdkit-cart-feedback__warning" d="M24 13v14m0 7h.01"></path></svg><div class="tcdkit-cart-feedback__body"><div role="status" aria-live="polite" aria-atomic="true"><strong data-kit-feedback-title></strong><p data-kit-feedback-detail></p></div><a class="tcdkit-cart-feedback__view" href="${escapeHtml(routes.cart || '/cart')}">${escapeHtml(copy.view)}</a></div><button class="tcdkit-cart-feedback__close" type="button" aria-label="${escapeHtml(copy.close)}">×</button>`;
      kitFeedback.querySelector('button').addEventListener('click', hideKitFeedback);
      document.body.append(kitFeedback);
      const dock = root.querySelector('.tcdkit-kit-mobile-dock');
      if (dock) {
        const measureDock = () => kitFeedback.style.setProperty('--tcdkit-feedback-dock-height', `${dock.getBoundingClientRect().height}px`);
        feedbackDockObserver = new ResizeObserver(measureDock);
        feedbackDockObserver.observe(dock);
        measureDock();
      }
    }
    kitFeedback.hidden = true;
    kitFeedback.dataset.status = savingConfirmed ? 'success' : 'warning';
    kitFeedback.querySelector('[data-kit-feedback-title]').textContent = copy.title;
    kitFeedback.querySelector('[data-kit-feedback-detail]').textContent = savingConfirmed
      ? copy.saving.replace('__PERCENT__', String(level))
      : refreshFailed ? copy.refresh : copy.unconfirmed;
    void kitFeedback.offsetWidth;
    kitFeedback.hidden = false;
    const basket = document.querySelector('[data-tcd-cart-trigger]');
    if (basket) {
      clearTimeout(basketPulseTimer);
      basket.classList.remove('tcdkit-cart-pulse');
      void basket.offsetWidth;
      basket.classList.add('tcdkit-cart-pulse');
      basketPulseTimer = setTimeout(() => basket.classList.remove('tcdkit-cart-pulse'), 900);
    }
  }

  async function addKitToCart() {
    if (demoSnapshot) return;
    const powers = selectedPowers();
    const level = discountLevel();
    if (state.busy || state.added || !powerComplete() || !toolsComplete() || !level) return;

    const kitId = createKitId();
    const discountValue = `${level}%`;
    const items = [];
    state.selectedTools.forEach((selection) => {
      items.push({
        id: Number(selection.variantId),
        quantity: selection.quantity,
        properties: {
          'Kit saving': discountValue,
          _tcd_kit_id: kitId,
          _tcd_kit_role: 'tool',
          _tcd_kit_mode: builderMode,
          _tcd_kit_brand: toolByVariant.get(String(selection.variantId))?.brand || builderMode,
          _tcd_kit_discount_level: String(level),
          _tcd_kit_platform: toolByVariant.get(String(selection.variantId))?.platform || '',
        },
      });
    });
    powers.forEach((power) => {
      items.push({
        id: Number(power.variantId),
        quantity: 1,
        properties: {
          'Kit saving': discountValue,
          _tcd_kit_id: kitId,
          _tcd_kit_role: 'set',
          _tcd_kit_mode: builderMode,
          _tcd_kit_brand: power.brand,
          _tcd_kit_discount_level: String(level),
          _tcd_kit_platform: power.platform,
        },
      });
    });

    let itemsAdded = false;
    state.busy = true;
    elements.status.textContent = '';
    hideKitFeedback();
    renderReview();
    try {
      const response = await fetch(jsonRoute(routes.cartAdd, '/cart/add'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || errorPayload.message || 'Unable to add the kit to the cart.');
      }
      itemsAdded = true;
      state.added = true;
      const cartResponse = await fetch(`${jsonRoute(routes.cart, '/cart')}?_tcd_kit_added=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('The kit was added, but the cart could not be refreshed.');
      let cart = await cartResponse.json();
      if (window.tcdKitIntegrity) cart = await window.tcdKitIntegrity.repair(cart);
      const kitLines = cart.items.filter(item => item.properties?._tcd_kit_id === kitId);
      const expectedSaving = kitLines.reduce((sum, item) => sum + Math.floor(item.original_line_price * level / 100), 0);
      const appliedSaving = kitLines.reduce((sum, item) => sum + appliedLineSaving(item), 0);
      state.added = true;
      elements.status.textContent = appliedSaving >= expectedSaving && expectedSaving > 0
        ? 'Your custom kit and automatic saving have been added to your basket.'
        : 'Your kit is in the basket, but the kit saving could not be confirmed. Please contact our team before paying.';
      showKitFeedback(appliedSaving >= expectedSaving && expectedSaving > 0, level);
      if (window.tcd?.cart) {
        try { await window.tcd.cart.refresh(); } catch {}
      }
      window.dispatchEvent(new CustomEvent('tcdkit:cart-updated', { detail: cart }));
      pushEvent('tcdkit_kit_builder_add', {
        tcdkit_kit_id: kitId,
        tcdkit_builder_mode: builderMode,
        tcdkit_platform: state.platform,
        tcdkit_tool_quantity: toolQuantity(),
        tcdkit_discount_percent: level,
        tcdkit_item_count: items.length,
      });
    } catch (error) {
      elements.status.textContent = error.message || 'Unable to add the kit. Please try again.';
      if (itemsAdded) showKitFeedback(false, level, true);
    } finally {
      state.busy = false;
      renderAll();
    }
  }

  elements.platformCards.forEach((card) => card.addEventListener('click', () => choosePlatform(card.dataset.tcdkitPlatform)));
  elements.stepButtons.forEach((button) => button.addEventListener('click', () => {
    if (!button.disabled) setStep(Number(button.dataset.tcdkitStepButton));
  }));
  root.querySelectorAll('[data-tcdkit-next]').forEach((button) => button.addEventListener('click', () => setStep(state.step + 1)));
  root.querySelectorAll('[data-tcdkit-back]').forEach((button) => button.addEventListener('click', () => setStep(state.step - 1)));
  root.querySelectorAll('[data-tcdkit-edit-step]').forEach((button) => button.addEventListener('click', () => setStep(Number(button.dataset.tcdkitEditStep))));

  elements.categories.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tcdkit-category]');
    if (!button) return;
    state.category = button.dataset.tcdkitCategory;
    renderTools();
  });

  elements.toolSearch.addEventListener('input', (event) => {
    state.search = event.target.value;
    renderTools();
  });

  elements.toolGrid.addEventListener('click', (event) => {
    if (event.target.closest('[data-tcdkit-view-product]')) return;
    const target = event.target.closest('[data-tcdkit-toggle-tool]');
    if (target) toggleTool(target.dataset.tcdkitToggleTool);
  });

  elements.powerGrid.addEventListener('click', (event) => {
    if (event.target.closest('[data-tcdkit-view-product]')) return;
    const target = event.target.closest('[data-tcdkit-choose-power]');
    if (target) choosePower(target.dataset.tcdkitChoosePower);
  });

  elements.reviewTools.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tcdkit-quantity]');
    if (button) changeQuantity(button.dataset.variantId, button.dataset.tcdkitQuantity);
  });

  elements.addKit.addEventListener('click', addKitToCart);
  elements.clearKitButtons.forEach((button) => button.addEventListener('click', clearKit));
  elements.summaryEditTools.addEventListener('click', () => {
    setMobileSummaryOpen(false);
    setStep(2);
  });
  elements.summaryAction.addEventListener('click', () => {
    setMobileSummaryOpen(false);
    runJourneyAction(elements.summaryAction);
  });
  elements.mobileAction.addEventListener('click', () => {
    setMobileSummaryOpen(false);
    runJourneyAction(elements.mobileAction);
  });
  elements.mobileSummary.addEventListener('click', () => {
    const open = !elements.summary.classList.contains('is-mobile-open');
    setMobileSummaryOpen(open);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setMobileSummaryOpen(false);
  });

  document.addEventListener('tcdkit:brand-mode-change', (event) => {
    setBuilderMode(event.detail?.mode);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target?.contains(root)) return;
    document.body.classList.remove('tcdkit-kit-builder-active', 'tcdkit-kit-summary-open');
    kitFeedback?.remove();
    feedbackDockObserver?.disconnect();
    clearTimeout(basketPulseTimer);
  });

  function showDemoStage(stage) {
    if (!demoSnapshot) return;
    const index = Math.max(0, Math.min(4, Number(stage) || 0));
    state.platform = index >= 2 ? (builderMode === 'metabo' ? '18v' : demoSnapshot.state.platform || '18v') : '';
    state.selectedTools.clear();
    state.selectedPowerIds = { hikoki: '', metabo: '' };
    state.category = 'All tools';
    state.search = '';
    state.busy = false;
    state.added = false;
    elements.status.textContent = '';
    if (index >= 3) {
      const candidates = tools.filter(compatibleTool);
      const chosen = builderMode === 'both'
        ? requiredPowerBrands().map(brand => candidates.find(item => item.brand === brand)).filter(Boolean)
        : candidates.slice(0, 2);
      chosen.forEach(item => state.selectedTools.set(String(item.variantId), {variantId: item.variantId, quantity: 1}));
      if (builderMode !== 'both' && chosen.length === 1 && selectionLimit(chosen[0]) >= 2) state.selectedTools.get(String(chosen[0].variantId)).quantity = 2;
    }
    if (index === 4) requiredPowerBrands().forEach(brand => {
      const power = powerSets.find(item => item.brand === brand && compatiblePower(item));
      if (power) state.selectedPowerIds[brand] = String(power.variantId);
    });
    state.step = Math.max(1, index);
    renderAll();
    setStep(state.step, false);
  }

  function beginDemo() {
    if (state.busy || demoSnapshot) return;
    demoSnapshot = {
      mode: builderMode,
      state: {...state, selectedTools: new Map([...state.selectedTools].map(([key, value]) => [key, {...value}])), selectedPowerIds: {...state.selectedPowerIds}},
      status: elements.status.textContent,
      summaryOpen: elements.summary.classList.contains('is-mobile-open'),
    };
    showDemoStage(0);
  }

  function endDemo() {
    if (!demoSnapshot) return;
    const original = demoSnapshot;
    builderMode = original.mode;
    Object.assign(state, original.state);
    renderTools();
    renderPowerSets();
    renderReview();
    renderAll();
    setStep(state.step, false);
    elements.status.textContent = original.status;
    setMobileSummaryOpen(original.summaryOpen);
    demoSnapshot = null;
    notifyGuide();
  }

  root.addEventListener('tcdkit:demo', event => {
    if (event.detail?.action === 'begin') beginDemo();
    if (event.detail?.action === 'show') showDemoStage(event.detail.stage);
    if (event.detail?.action === 'end') endDemo();
  });

  root.addEventListener('tcdkit:guide-request', notifyGuide);
  root.addEventListener('tcdkit:guide-go', event => {
    if (!state.busy) setStep(event.detail?.step, false);
  });

  restoreState();
  if (builderMode === 'metabo' && state.platform && state.platform !== '18v') state.platform = '18v';
  renderAll();
  setStep(state.step, false);
})();





